/**
 * connectorsHttp — the real, HTTP-backed `ConnectorsApi` implementation.
 *
 * This is the swap the honest default (`honestConnectorsApi`) always anticipated:
 * `list` and `disconnect` now talk to the store-back `/v1/connectors` routes; the
 * surface (`ConnectorsSection`, `useConnectors`) and types are unchanged.
 *
 * Two honesty guarantees live here, mirroring the backend:
 *  - `connect` NEVER fabricates a connection. The backend returns an explicit
 *    "provider not configured" result today, so `connect` resolves only if the
 *    backend confirms a real `connected` state — otherwise it throws
 *    `ConnectorUnavailableError`, exactly like the honest default.
 *  - `list` degrades to the honest `coming_soon` catalogue when the backend is
 *    unreachable or returns nothing usable: we cannot confirm a connection, so we
 *    never show one, and we never blank the surface with an error.
 *
 * Server payloads are untrusted: every field is validated against the known id /
 * status vocabulary before it reaches the UI (an unknown status becomes `error`,
 * never a silent `connected`).
 *
 * See `docs/connectors-backend.md` for the backend contract.
 */
import { apiClient } from '@/api/client';
import {
  CONNECTOR_CATALOGUE,
  ConnectorUnavailableError,
  type Connector,
  type ConnectorId,
  type ConnectorState,
  type ConnectorStatus,
  type ConnectorsApi,
} from './connectors';

/** Minimal HTTP surface this client needs — satisfied by the shared axios instance. */
export interface ConnectorsHttpResponse {
  data: unknown;
}
export interface ConnectorsHttpClient {
  get(url: string): Promise<ConnectorsHttpResponse>;
  delete(url: string): Promise<ConnectorsHttpResponse>;
  post(url: string, body?: unknown): Promise<ConnectorsHttpResponse>;
}

const KNOWN_IDS: ReadonlySet<string> = new Set(CONNECTOR_CATALOGUE.map((c) => c.id));
const KNOWN_STATUSES: ReadonlySet<string> = new Set<ConnectorStatus>([
  'coming_soon',
  'disconnected',
  'connected',
  'error',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Parse one untrusted server payload into a ConnectorState, or null if unusable. */
function toConnectorState(raw: unknown): ConnectorState | null {
  if (!isRecord(raw)) return null;
  const id = raw.id;
  if (typeof id !== 'string' || !KNOWN_IDS.has(id)) return null;
  const rawStatus = raw.status;
  const status: ConnectorStatus =
    typeof rawStatus === 'string' && KNOWN_STATUSES.has(rawStatus)
      ? (rawStatus as ConnectorStatus)
      : 'error';
  const detail = typeof raw.detail === 'string' ? raw.detail : undefined;
  return { id: id as ConnectorId, status, ...(detail ? { detail } : {}) };
}

function extractArray(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (isRecord(raw) && Array.isArray(raw.connectors)) return raw.connectors;
  return [];
}

/** The honest catalogue: every entry as `coming_soon` — never a fabricated connection. */
function honestFallback(): ConnectorState[] {
  return CONNECTOR_CATALOGUE.map((c: Connector) => ({ id: c.id, status: 'coming_soon' as const }));
}

export function createHttpConnectorsApi(client: ConnectorsHttpClient): ConnectorsApi {
  return {
    async list(): Promise<ConnectorState[]> {
      try {
        const res = await client.get('/connectors');
        const states = extractArray(res.data)
          .map(toConnectorState)
          .filter((s): s is ConnectorState => s !== null);
        return states.length > 0 ? states : honestFallback();
      } catch {
        return honestFallback();
      }
    },

    async connect(id: ConnectorId): Promise<ConnectorState> {
      const res = await client.post(`/connectors/${id}/connect`);
      const state = toConnectorState(res.data);
      // Only a backend-confirmed live connection is real; the honest
      // "provider not configured" result must never become a fake connection.
      if (state && state.status === 'connected') return state;
      throw new ConnectorUnavailableError(id);
    },

    async disconnect(id: ConnectorId): Promise<ConnectorState> {
      const res = await client.delete(`/connectors/${id}`);
      return toConnectorState(res.data) ?? { id, status: 'coming_soon' };
    },
  };
}

/** App-wired instance: the shared axios client carries the JWT + SSO cookies. */
export const httpConnectorsApi: ConnectorsApi = createHttpConnectorsApi({
  get: (url) => apiClient.get(url),
  delete: (url) => apiClient.delete(url),
  post: (url, body) => apiClient.post(url, body),
});
